import React from 'react';
import Button from './ui/Button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// App-level error boundary. Without one, any exception thrown during render
// unmounts the entire React tree and leaves a permanently blank page.
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Unhandled render error:', error, errorInfo.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="max-w-lg w-full bg-white border rounded-lg p-6 text-center">
            <h1 className="text-lg font-semibold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-sm text-gray-600 mb-4">
              The page hit an unexpected error. Reloading usually fixes it.
            </p>
            <p className="text-xs text-gray-700 font-mono mb-4 wrap-break-word">
              {this.state.error.message}
            </p>
            <Button
              variant="primary"
              onClick={() => window.location.reload()}
              title="Load this page again from the start; the error usually clears on a fresh load"
            >
              Reload page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
